import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Numeric } from '@swyftx/aviary/atoms/Typography';
import { useTailwindBreakpoint } from '@swyftx/aviary/hooks/useTailwindBreakpoint';
import { EnhancedTableData, EnhancedTableHeaderData, EnhancedTableSort } from '@swyftx/aviary/molecules/EnhancedTable';
import { MarginPositionsQuery, ProtectionOrderVariant } from '@swyftx/margin/build/types';
import { MarginPosition, MarginPositionStatus } from '@swyftx/margin/build/types';

import { Modals } from '@global-components/Modals/Modals.enum';
import { useModal } from '@global-components/Modals/useModal.hooks';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { EstimatedMarginRequired, MarginInstrument, MarginSide } from '@routes/SwyftxMargin/components/General';

import { DateTime } from 'luxon';
import { getGroupByTitle } from 'src/lib/dynamic-price-alerts/utils/dynamic-price-alerts.utils';
import { useMarginInstruments } from 'src/lib/margin/hooks/instruments';
import { useMarginPositions } from 'src/lib/margin/hooks/positions';
import { MarginOrdersType, MarginPositionDetailsStep } from 'src/lib/margin/types';
import { getMarketPrecisionScale } from 'src/lib/margin/utils';

import { MarginPositionDetailsProtectionOrder } from '../../../components';
import { MarginPositionRowType, MarginPositionsTableData } from '../MarginPositionsTable.data';
import {
  MarginPositionsTableAverageFillCurrentPrice,
  MarginPositionsTableCommission,
  MarginPositionsTableInstrumentSide,
  MarginPositionsTableLotsProfitLoss,
  MarginPositionsTableOpenedTime,
  MarginPositionsTableOvernightAdmin,
  MarginPositionsTablePrice,
  MarginPositionsTablePriceCurrentPrice,
  MarginPositionsTableStopLossTakeProfit,
  MarginPositionsTableType,
  MarginPositionsTableUsedMargin,
} from '../components';
import { MarginPositionsTableActions } from '../components/MarginPositionsTableActions';
import { MarginPositionsTableCurrentPrice } from '../components/MarginPositionsTableCurrentPrice';
import { MarginPositionsTableProfitLoss } from '../components/MarginPositionsTableProfitLoss';

type MarginPositionsHeaderData = { [key in keyof MarginPositionsTableData]: EnhancedTableHeaderData };

type Props = {
  pageSize?: number;
  ordersType: MarginOrdersType;
  filterCurrentInstrument?: string;
  sortKey?: keyof MarginPositionsTableData;
  isSm?: boolean;
  isXs?: boolean;
};

const useMarginPositionsTable = ({
  pageSize = 300,
  ordersType,
  filterCurrentInstrument,
  sortKey = 'lastUpdated',
  isXs,
  isSm,
}: Props) => {
  const { t } = useTranslation('margin');
  const [sort, setSort] = useState<EnhancedTableSort<MarginPositionsTableData>>({
    sortKey,
    sortDirection: 'DESC',
  });

  const [sortDir, setSortDir] = useState<MarginPositionsQuery['sortDir']>('desc');

  const status = useMemo(() => {
    switch (ordersType) {
      case MarginOrdersType.Orders:
        return [MarginPositionStatus.Pending];
      case MarginOrdersType.Position:
        return [MarginPositionStatus.Open];
      case MarginOrdersType.History:
      case MarginOrdersType.Transfers:
      case MarginOrdersType.BalanceAdjustments:
        return [];
      default:
        ordersType satisfies never;
    }
  }, [ordersType]);

  const { positions, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage, isFetching, isError } =
    useMarginPositions({
      pageSize,
      status,
      // sortBy,
      sortDir,
    });

  const { getMarketByPosition } = useMarginInstruments();
  const { openModal } = useModal();
  const isDesktopView = useTailwindBreakpoint('md', true);

  const openDetailsModal = useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      positionId: string,
      initialStep: MarginPositionDetailsStep,
      protectionOrderType?: ProtectionOrderVariant,
    ) => {
      e.preventDefault();
      e.stopPropagation();

      openModal(Modals.MarginPositionDetails, {
        initialStep,
        positionId,
        protectionOrderType,
      });
    },
    [openModal],
  );

  const headers: MarginPositionsHeaderData = useMemo(
    () => ({
      instrument: {
        title: t('tables.positions.headers.instrument'),
        alignment: 'start',
        sortable: false,
        frozen: true,
        insetLeft: true,
        offsetLeft: 0,
        showFrozenDivider: false,
        className: 'min-w-[164px] max-w-[164px]',
        enabled: isDesktopView,
      },
      type: {
        title: t('tables.positions.headers.type'),
        alignment: 'start',
        sortable: false,
        frozen: true,
        showFrozenDivider: false,
        offsetLeft: 164,
        className: 'min-w-[128px] max-w-[128px]',
        enabled: isDesktopView && ordersType === MarginOrdersType.Orders,
      },
      side: {
        title: t('tables.positions.headers.side'),
        alignment: 'start',
        sortable: false,
        frozen: true,
        className: 'min-w-[128px] max-w-[128px]',
        offsetLeft: ordersType === MarginOrdersType.Orders ? 225 : 150,
        showFrozenDivider: true,
        enabled: isDesktopView,
      },
      lots: {
        title: t('tables.positions.headers.sizeLots'),
        tooltip: t('tradePanel.tooltips.sizeLots'),
        className: 'min-w-[164px]',
        alignment: 'end',
        sortable: false,
        insetLeft: true,
        enabled: isDesktopView,
      },

      price: {
        title: t('tables.positions.headers.price'),
        className: 'min-w-[164px]',
        alignment: 'end',
        sortable: false,
        enabled: isDesktopView && ordersType === MarginOrdersType.Orders,
      },

      avgFillPrice: {
        title: t('tables.positions.headers.avgFillPrice'),
        tooltip: t('position.averageFillPrice.tooltip'),
        className: 'min-w-[164px]',
        alignment: 'end',
        sortable: false,
        enabled: isDesktopView && [MarginOrdersType.Position].includes(ordersType),
      },

      stopLossPrice: {
        title: t('tables.positions.headers.stopLossPrice'),
        className: 'min-w-[164px]',
        alignment: 'end',
        sortable: false,
        enabled: isDesktopView,
      },

      takeProfitPrice: {
        title: t('tables.positions.headers.takeProfitPrice'),
        className: 'min-w-[164px]',
        alignment: 'end',
        sortable: false,
        enabled: isDesktopView,
      },

      currentPrice: {
        title: t('tables.positions.headers.currentPrice'),
        className: 'min-w-[164px]',
        alignment: 'end',
        sortable: false,
        enabled: isDesktopView,
      },

      estimatedMarginRequired: {
        title: t('tables.positions.headers.estimatedMarginRequired'),
        className: 'min-w-[204px]',
        alignment: 'end',
        sortable: false,
        enabled: isDesktopView && ordersType === MarginOrdersType.Orders,
      },

      profitLoss: {
        title: t('tables.positions.headers.profitLoss'),
        tooltip: t('position.profitLoss.tooltip'),
        className: 'min-w-[204px]',
        alignment: 'end',
        sortable: false,
        enabled: isDesktopView && [MarginOrdersType.Position].includes(ordersType),
      },

      usedMargin: {
        title: t('tables.positions.headers.usedMargin'),
        tooltip: t('position.usedMargin.tooltip'),
        className: 'min-w-[204px]',
        alignment: 'end',
        sortable: false,
        enabled: isDesktopView && [MarginOrdersType.Position].includes(ordersType),
      },

      commission: {
        title: t('tables.positions.headers.commission'),
        tooltip: t('position.commission.tooltip'),
        className: 'min-w-[164px] max-w-[164px]',
        alignment: 'end',
        sortable: false,
        enabled: isDesktopView && [MarginOrdersType.Position].includes(ordersType),
      },

      overnightAdmin: {
        title: t('tables.positions.headers.overnightAdmin'),
        className: 'min-w-[156px] max-w-[156px]',
        alignment: 'end',
        sortable: false,
        enabled: isDesktopView && [MarginOrdersType.Position].includes(ordersType),
      },

      lastUpdated: {
        title: t('tables.positions.headers.lastUpdated'),
        alignment: 'end',
        sortable: true,
        enabled: isDesktopView && [MarginOrdersType.Position, MarginOrdersType.Orders].includes(ordersType),
      },

      positionId: {
        title: t('tables.positions.headers.positionId'),
        alignment: 'end',
        sortable: false,
        enabled: isDesktopView && [MarginOrdersType.Position].includes(ordersType),
      },

      actions: {
        title: t('tables.positions.headers.actions'),
        alignment: 'end',
        sortable: false,
        insetRight: true,
        offsetRight: 0,
        showFrozenDivider: true,
        frozen: true,
        enabled: isDesktopView && ordersType !== MarginOrdersType.History,
      },

      instrumentSide: {
        title: t('tables.positions.headers.instrumentSide'),
        alignment: 'start',
        sortable: false,
        insetRight: true,
        enabled: !isDesktopView && [MarginOrdersType.Position, MarginOrdersType.Orders].includes(ordersType),
      },

      averageFillCurrentPrice: {
        title: t('tables.positions.headers.averageFillCurrentPrice'),
        alignment: 'end',
        sortable: false,
        insetRight: true,
        enabled: isSm && ordersType === MarginOrdersType.Position,
      },

      priceCurrentPrice: {
        title: t('tables.positions.headers.priceCurrentPrice'),
        alignment: 'end',
        sortable: false,
        insetRight: true,
        enabled: isSm && ordersType === MarginOrdersType.Orders,
      },

      stopLossTakeProfit: {
        title: t('tables.positions.headers.stopLossTakeProfit'),
        alignment: 'end',
        sortable: false,
        insetRight: true,
        enabled: isSm && [MarginOrdersType.Position, MarginOrdersType.Orders].includes(ordersType),
      },

      lotsProfitLoss: {
        title:
          ordersType === MarginOrdersType.Position
            ? t('tables.positions.headers.lotsProfitLoss')
            : t('tables.positions.headers.sizeLots'),
        alignment: 'end',
        sortable: false,
        insetRight: true,
        enabled: !isDesktopView && [MarginOrdersType.Position, MarginOrdersType.Orders].includes(ordersType),
      },
    }),
    [isDesktopView, isSm, ordersType, t],
  );

  const filteredPositions = useMemo(() => {
    if (!positions) return [];

    return positions.filter((p) => {
      const instrumentFilter =
        !filterCurrentInstrument || (filterCurrentInstrument && p.marketUuid === filterCurrentInstrument);

      switch (ordersType) {
        case MarginOrdersType.Position:
          const isOpen = p.status === MarginPositionStatus.Open;
          return isOpen && instrumentFilter;
        case MarginOrdersType.Orders:
          const isPending = [
            MarginPositionStatus.Limit,
            MarginPositionStatus.Stop,
            MarginPositionStatus.Pending,
          ].includes(p.status);

          return isPending && instrumentFilter;
        case MarginOrdersType.History:
          const isHistoric = [
            MarginPositionStatus.Closed,
            MarginPositionStatus.Reject,
            MarginPositionStatus.Cancelled,
          ].includes(p.status);
          return isHistoric && instrumentFilter;
        case MarginOrdersType.Transfers:
        case MarginOrdersType.BalanceAdjustments:
          return false;
        default:
          ordersType satisfies never;
          return false;
      }
    });
  }, [filterCurrentInstrument, ordersType, positions]);

  const buildDataItem = useCallback(
    (position: MarginPosition, type: MarginPositionRowType = 'position') => {
      const market = getMarketByPosition(position);

      const lots = position.filledOpen
        ? Big(position.filledOpen).minus(position.filledClose || 0)
        : Big(position.contracts);
      let price = Big(position.openPrice || position.limitPrice || position.stopPrice || '0');
      if (position.limitPrice && Big(position.limitPrice).gt(0)) price = Big(position.limitPrice);
      if (position.stopPrice && Big(position.stopPrice).gt(0)) price = Big(position.stopPrice);

      return {
        value: position,
        instrument: {
          value: position.marketUuid,
          element: <MarginInstrument market={market} size='medium' />,
        },
        type: {
          value: price.toString(),
          element: <MarginPositionsTableType positionId={position.positionId} />,
        },
        side: {
          value: position.sell,
          element: <MarginSide positionId={position.positionId} />,
        },
        lots: {
          value: lots,
          element: (
            <FlexLayout justifyContent='end'>
              <Numeric>
                {formatCurrency(lots, undefined, {
                  priceScale: getMarketPrecisionScale(market?.minLotIncrement),
                })}
              </Numeric>
            </FlexLayout>
          ),
        },

        price: {
          value: price.toString(),
          element: (
            <FlexLayout justifyContent='end'>
              <MarginPositionsTablePrice positionId={position.positionId} />
            </FlexLayout>
          ),
        },

        avgFillPrice: {
          value: position.openPrice,
          element: (
            <FlexLayout justifyContent='end'>
              <Numeric>{position.openPrice ? formatCurrency(position.openPrice) : '-'}</Numeric>
            </FlexLayout>
          ),
        },

        stopLossPrice: {
          value: position.stopLoss,
          element: (
            <MarginPositionDetailsProtectionOrder
              position={position}
              protectionOrderType={ProtectionOrderVariant.StopLoss}
              size='small'
              onClick={(e) =>
                openDetailsModal(
                  e,
                  position.positionId,
                  MarginPositionDetailsStep.ManageProtectionOrders,
                  ProtectionOrderVariant.StopLoss,
                )
              }
            />
          ),
        },

        takeProfitPrice: {
          value: position.takeProfit,
          element: (
            <MarginPositionDetailsProtectionOrder
              position={position}
              protectionOrderType={ProtectionOrderVariant.TakeProfit}
              size='small'
              onClick={(e) =>
                openDetailsModal(
                  e,
                  position.positionId,
                  MarginPositionDetailsStep.ManageProtectionOrders,
                  ProtectionOrderVariant.TakeProfit,
                )
              }
            />
          ),
        },

        currentPrice: {
          value: position.currentPrice,
          element: (
            <FlexLayout spacing={4} justifyContent='end'>
              <MarginPositionsTableCurrentPrice positionId={position.positionId} />
            </FlexLayout>
          ),
        },

        estimatedMarginRequired: {
          value: '',
          element: (
            <FlexLayout justifyContent='end'>
              <EstimatedMarginRequired lots={position.volume} market={market} price={price} />
            </FlexLayout>
          ),
        },

        profitLoss: {
          value: '',
          element: <MarginPositionsTableProfitLoss positionId={position.positionId} />,
        },

        usedMargin: {
          value: position.marginUsed,
          element: <MarginPositionsTableUsedMargin positionId={position.positionId} />,
        },

        commission: {
          value: position.commission,
          element: <MarginPositionsTableCommission positionId={position.positionId} />,
        },

        overnightAdmin: {
          value: position.swap,
          element: (
            <FlexLayout spacing={4} justifyContent='end'>
              <MarginPositionsTableOvernightAdmin positionId={position.positionId} />
            </FlexLayout>
          ),
        },

        lastUpdated: {
          value: position.lastUserAction,
          groupByTitle: getGroupByTitle(DateTime.fromMillis(position.lastUserAction || 0)),
          element: (
            <FlexLayout justifyContent='end'>
              <MarginPositionsTableOpenedTime key={position.positionId} value={position.lastUserAction} />
            </FlexLayout>
          ),
        },

        positionId: {
          value: type === 'position' ? position.positionIdCode : '',
          element: (
            <FlexLayout justifyContent='end'>
              <Numeric className='whitespace-nowrap'>{position.positionIdCode}</Numeric>
            </FlexLayout>
          ),
        },

        actions: {
          value: '',
          element: <MarginPositionsTableActions positionId={position.positionId} />,
        },

        instrumentSide: {
          value: position.lastUserAction || 0,
          groupByTitle: getGroupByTitle(DateTime.fromMillis(position.lastUserAction || 0)),
          element: <MarginPositionsTableInstrumentSide positionId={position.positionId} positionType={type} />,
        },

        averageFillCurrentPrice: {
          value: '',
          element: <MarginPositionsTableAverageFillCurrentPrice positionId={position.positionId} />,
        },

        priceCurrentPrice: {
          value: '',
          element: <MarginPositionsTablePriceCurrentPrice positionId={position.positionId} />,
        },

        stopLossTakeProfit: {
          value: '',
          element: <MarginPositionsTableStopLossTakeProfit positionId={position.positionId} />,
        },

        lotsProfitLoss: {
          value: '',
          element: <MarginPositionsTableLotsProfitLoss positionId={position.positionId} positionType={type} />,
        },
      };
    },
    [getMarketByPosition, openDetailsModal],
  );

  const data: EnhancedTableData<MarginPositionsTableData, MarginPosition>[] = useMemo(() => {
    const positionData = filteredPositions.map((position: MarginPosition) => buildDataItem(position));

    if (isXs) {
      const takeProfitStopLossPositions = positionData.filter(
        (p) => Big(p.value.takeProfit || '').gt(0) || Big(p.value.stopLoss || '').gt(0),
      );

      takeProfitStopLossPositions.forEach((p) => {
        const index = positionData.findIndex((pos) => pos.positionId === p.positionId);

        if (Big(p.value.stopLoss || '').gt(0)) {
          positionData.splice(index + 1, 0, buildDataItem(p.value, 'stoploss'));
        } else if (Big(p.value.takeProfit || '').gt(0)) {
          positionData.splice(index + 1, 0, buildDataItem(p.value, 'takeprofit'));
        }
      });
    }

    // Insert Take profit and stop loss items

    return positionData;
  }, [buildDataItem, filteredPositions, isXs]);

  const onSort = useCallback(
    (newSort?: EnhancedTableSort<MarginPositionsTableData>) => {
      const tableData: EnhancedTableData<MarginPositionsTableData, MarginPosition>[] = Object.assign([], data);

      if (!newSort) return tableData;

      setSort(newSort);

      setSortDir(newSort.sortDirection === 'DESC' ? 'desc' : 'asc');

      return tableData;
    },
    [data],
  );

  return {
    initialSort: sort,
    isError,
    headers,
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    isFetching,
    onSort,
    fetchNextPage,
  };
};

export { useMarginPositionsTable };
